import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, mergeMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Instituicao } from 'src/app/models/instituicao';
import { Estado } from 'src/app/models/estado';
import { Municipio } from 'src/app/models/municipio';
import { Curso } from 'src/app/models/curso';
import { Eixo } from 'src/app/models/eixo';

/**
 * class APIService
 */
@Injectable({ providedIn: 'root' })
export class APIService {
  // url de conecção com a API
  baseUrl = environment.baseURL;

  constructor(private http: HttpClient) { }

  getCurso(coursoId: number) {
    return this.http.get<{}>(`${this.baseUrl}/cursos/${coursoId}`).pipe(
      mergeMap((curso: Curso) => {
        const eixoId = curso.eixoTecnologicoCnctId;
        return this.http.get(`${this.baseUrl}/eixos/${eixoId}`).pipe(
          map((eixo: Eixo) => {
            return {
              ...curso, eixo: { nome: eixo.nome, id: eixo.id },
            };
          }),
        );
      }),
    );
  }

  getEixos() {
    return this.http.get<[]>(`${this.baseUrl}/eixos/`).pipe(
      map((eixos) => {
        eixos.map((eixo: Eixo) => {
          return { id: eixo.id, nome: eixo.nome, eixo: eixo.linkThumb };
        });
        return eixos;
      }),
    );
  }

  getEixo(id: number) {
    return this.http.get<{}>(`${this.baseUrl}/eixos/${id}`);
  }

  getFaq(query?: number, numeroPagina?: number) {
    const str = query ? query.toString() : '';
    const numeroPg = numeroPagina ? numeroPagina.toString() : '1';

    let paramsBusca = new HttpParams().set('q', str);
    paramsBusca = paramsBusca.append('pagina', numeroPg);

    return this.http.get<any[]>(`${this.baseUrl}/faq/busca`, {
      params: paramsBusca,
    });
  }

  getCursos(
    eixoId?: number,
    areaId?: number,
    numeroPagina?: number,
    query?: string,
    filtro?: string,
    ordenacao?: string,
  ) {
    const EIXOID = eixoId && eixoId.toString();
    const AREAID = areaId && areaId.toString();
    const numeroPg = numeroPagina ? numeroPagina.toString() : '1';
    let queryParams = new HttpParams();

    if (EIXOID) {
      queryParams = queryParams.append('eixo', EIXOID);
    }

    if (AREAID) {
      queryParams = queryParams.append('areatecnologica', AREAID);
    }

    queryParams = queryParams.append('q', query ? query : '');
    queryParams = queryParams.append('filtro', filtro ? filtro : '');
    queryParams = queryParams.append('ordenacao', ordenacao ? ordenacao : '');
    queryParams = queryParams.append('pagina', numeroPg);

    return this.http.get<{}>(`${this.baseUrl}/cursos/busca/`, {
      params: queryParams,
    });
  }

  /**
   * retorna os cursos que mudaram de nome
   *
   */
  getCursosTabelaConvergencia(
    eixoId?: number,
    numeroPagina?: number,
    query?: string,
    filtro?: string,
    ordenacao?: string,
  ) {
    const EIXOID = eixoId && eixoId.toString();
    const numeroPg = numeroPagina ? numeroPagina.toString() : '1';
    let queryParams = new HttpParams();
    if (EIXOID) {
      queryParams = queryParams.append('eixo', EIXOID);
    }
    queryParams = queryParams.append('q', query ? query : '');
    queryParams = queryParams.append('filtro', filtro ? filtro : '');
    queryParams = queryParams.append('ordenacao', ordenacao ? ordenacao : '');
    queryParams = queryParams.append('pagina', numeroPg);
    return this.http.get<{}>(`${this.baseUrl}/cursos/buscaCursosMudaramNome/`, {
      params: queryParams,
    });
  }

  /**
   * retorna os cursos não incluídos ou excluídos - tabela submissão
   *
   */
  getCursosTabelaSubmissao(
    eixoId?: number,
    numeroPagina?: number,
    query?: string,
    filtro?: string,
    ordenacao?: string,
  ) {
    const EIXOID = eixoId && eixoId.toString();
    const numeroPg = numeroPagina ? numeroPagina.toString() : '1';
    let queryParams = new HttpParams();
    if (EIXOID) {
      queryParams = queryParams.append('eixo', EIXOID);
    }
    queryParams = queryParams.append('q', query ? query : '');
    queryParams = queryParams.append('filtro', filtro ? filtro : '');
    queryParams = queryParams.append('ordenacao', ordenacao ? ordenacao : '');
    queryParams = queryParams.append('pagina', numeroPg);
    return this.http.get<{}>(`${this.baseUrl}/cursos/buscaCursosSubmissao/`, {
      params: queryParams,
    });
  }

  getBusca(query?: string, numeroPagina?: number) {
    const str = query ? query : '';
    const numeroPg = numeroPagina ? numeroPagina.toString() : '1';

    let parametrosBusca = new HttpParams().set('q', str);
    parametrosBusca = parametrosBusca.append('pagina', numeroPg);
    return this.http.get<any[]>(`${this.baseUrl}/pesquisa/busca`, {
      params: parametrosBusca,
    });
  }

  getPrincipalInfo() {
    return this.http.get<any[]>(`${this.baseUrl}/home/`);
  }

  getCatalogoInfo() {
    return this.http.get<any[]>(`${this.baseUrl}/catalogo/`);
  }

  postForm(form) {
    return this.http.post(`${this.baseUrl}/consultapublica`, form);
  }

  getCursosNaoPaginado(eixoId: number) {
    return this.http.get(`${this.baseUrl}/cursos/buscaporeixo/${eixoId}`);
  }

  getTermos() {
    return this.http.get('assets/documents/lista_de_termos.txt', { responseType: 'text' });
  }

  getInstituicosPorCurso(nomeCurso?: string) {
    return this.http.get<Instituicao[]>(`${this.baseUrl}/instituicao/buscaPorNomeCurso/${nomeCurso}`);
  }

  getInstituicosPorCursoUFeMunicipio(nomeCurso?: string, siglaUF?: string, codMunicipio?: string) {
    /* tslint:disable-next-line:max-line-length */
    return this.http.get<Instituicao[]>(`${this.baseUrl}/instituicao/buscaPorNomeCursoUFeMunicipio/${nomeCurso}/${siglaUF}/${codMunicipio}`);
  }

  getInstituicosPorCursoUF(nomeCurso?: string, siglaUF?: string) {
    return this.http.get<Instituicao[]>(`${this.baseUrl}/instituicao/buscaPorNomeCursoUF/${nomeCurso}/${siglaUF}`);
  }

  getEstados(nomeCurso?: string) {
    return this.http.get<Estado[]>(`${this.baseUrl}/estados/${nomeCurso}`);
  }

  getMunicipiosPorUF(nomeCurso?: string, uf?: string) {
    return this.http.get<Municipio[]>(`${this.baseUrl}/municipioPorUF/${nomeCurso}/${uf}`);
  }

  getAPIOlindaMunicipiosPorUF(uf?: string) {
    return this.http.get<Municipio[]>(`${this.baseUrl}/municipios/${uf}`);
  }

  getFiltroInstituicao(nomeCurso?: string, estados?: string, municipios?: string, tipos?: string) {
    let queryParams = new HttpParams();

    queryParams = queryParams.append('nomeCurso', nomeCurso ? nomeCurso : '');
    queryParams = queryParams.append('estados', estados ? estados : '');
    queryParams = queryParams.append('municipios', municipios ? municipios : '');
    queryParams = queryParams.append('tipos', tipos ? tipos : '');
    return this.http.get<Instituicao[]>(`${this.baseUrl}/instituicao/filtroInstituicao/`, {
      params: queryParams,
    });

  }

  getInstituicaoPorNome(nomeCurso?: string, nomeInstituicao?: string) {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('nomeCurso', nomeCurso ? nomeCurso : '');
    queryParams = queryParams.append('nomeInstituicao', nomeInstituicao ? nomeInstituicao : '');

    return this.http.get<Instituicao[]>(`${this.baseUrl}/instituicao/filtroInstituicaoPorNome/`, {
      params: queryParams,
    });

  }

  getQuantidadeInstituicoesPorNomeCurso(nomeCurso?: string) {
    return this.http.get<number>(`${this.baseUrl}/instituicao/buscaQuantidadePorNomeCurso/${nomeCurso}`);
  }

  /** Busca versão do sistema */
  getVersion() {
    return this.http.get(`${this.baseUrl}/version`, { responseType: 'text' });
  }

  /** Busca pdf cursos convergentes */
  getDownloadPdfCursosConvergentes() {
    return this.http.get(`${this.baseUrl}/pdf-cursos-convergentes/`, { responseType: 'blob' });
  }

  /** Busca pdf cursos submissao */
  getDownloadPdfCursosSubmissao() {
    return this.http.get(`${this.baseUrl}/pdf-cursos-submissao/`, { responseType: 'blob' });
  }

  /** Busca csv cursos convergentes */
  getDownloadCsvCursosConvergentes() {
    return this.http.get(`${this.baseUrl}/csv-cursos-convergentes/`, { responseType: 'blob' });
  }

  /** Busca csv cursos submissao */
  getDownloadCsvCursosSubmissao() {
    return this.http.get(`${this.baseUrl}/csv-cursos-submissao/`, { responseType: 'blob' });
  }

  /**
   *
   * @param eixoId id do eixo.
   * @param numeroPagina número da página.
   * @param query parâmetros de consulta.
   * @param filtro filtro de consulta.
   * @param ordenacao tipo de ordenação.
   * @returns retorna a area tecnológica.
   */
  getAreasTecnologicasPg(
    eixoId?: number,
    numeroPagina?: number,
    query?: string,
    filtro?: string,
    ordenacao?: string,
  ) {
    const EIXOID = eixoId && eixoId.toString();
    const numeroPg = numeroPagina ? numeroPagina.toString() : '1';
    let queryParams = new HttpParams();

    if (EIXOID) {
      queryParams = queryParams.append('eixo', EIXOID);
    }

    queryParams = queryParams.append('q', query ? query : '');
    queryParams = queryParams.append('filtro', filtro ? filtro : '');
    queryParams = queryParams.append('ordenacao', ordenacao ? ordenacao : '');
    queryParams = queryParams.append('pagina', numeroPg);

    return this.http.get<{}>(`${this.baseUrl}/eixos/${eixoId}/areas-tecnologicas/`, {
      params: queryParams
    });
  }

  getAreasTecnologicasByEixo(id: number) {
    return this.http.get<{}>(`${this.baseUrl}/eixos/${id}/areas-tecnologicas`);
  }

  getAreaTecnologicaById(id: number) {
    return this.http.get<{}>(`${this.baseUrl}/areas/${id}/`);
  }

  /**
   * Baixa csv de instituições ofertantes por curso
   * @param nomeCurso Nome do curso
   * @param encoding Codificação dos caracteres, aqui está usando ISO-8859-1
   * @returns Intituições de ensino
   */
  getDownloadCsvInstituicoesOfertantesByCurso(nomeCurso: string, encoding?: string) {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('encoding', encoding ? encoding : '');

    return this.http.get(`${this.baseUrl}/csv-instituicoes-ofertantes/${nomeCurso}`, {
      params: queryParams,
      responseType: 'blob'
    });
  }

  /**
   *
   * @param encoding encoda o texto
   * @returns Ctálogo em csv
   */
  getDownloadCsvCatalogoCnct(encoding?: string) {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('encoding', encoding ? encoding : '');

    return this.http.get(`${this.baseUrl}/csv-catalogo/`, {
      params: queryParams,
      responseType: 'blob'
    });
  }

  getLegislacaoInfo() {
    return this.http.get<any[]>(`${this.baseUrl}/legislacoes/cnct`);
  }

  /**
   * retorna os cursos convergentes
   *
   */
  getCursosConvergentes() {
    return this.http.get<{}>(`${this.baseUrl}/cursos/buscaCursosConvergentes/`);
  }

}
